import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtDate } from "../lib/api";
import { Link2, RefreshCw, Plug, AlertCircle, CheckCircle2, X, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useBilling } from "../context/BillingContext";
import confetti from "canvas-confetti";

/**
 * BrokerConnect — manages SnapTrade broker linking + manual resync.
 *
 * Props:
 *  - variant: "button" (Holdings header) | "card" (Settings page)
 *  - onSynced?: callback after a successful sync (refetch holdings)
 *
 * Three problems this component solves vs. previous version:
 *  1. Opens SnapTrade in a normal tab (NOT a popup), so when the user switches
 *     tabs to look up broker credentials the connection window doesn't get
 *     dismissed by focus-loss.
 *  2. Watches for a postMessage from /broker/callback (sent by SnapTrade's
 *     custom_redirect target) AND polls window.closed as a fallback.
 *  3. Renders a persistent "Waiting for broker..." overlay in the parent so
 *     the user always knows what's happening, with a Cancel/Manual-Sync option.
 *  4. Pops confetti + victory toast when at least one account gets synced.
 *  5. Button visibly switches to a green "BROKER CONNECTED" state when there
 *     are any synced accounts (not just when there are holdings).
 */
export default function BrokerConnect({ variant = "card", onSynced }) {
  const { tier, openUpgrade } = useBilling();
  const isPro = tier === "pro" || tier === "founder_lifetime";

  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [waitingForBroker, setWaitingForBroker] = useState(false);
  const brokerTabRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/broker/snaptrade/status");
      setStatus(data);
      return data;
    } catch (e) {
      void e;
      return null;
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Registered = we have a SnapTrade userSecret stored (created via /register).
  // Connected = SnapTrade has at least one linked broker account.
  // These two states are independent — you can be registered without any
  // connected accounts (e.g., if the broker portal crashed before linking
  // completed, or if you DISCONNECT-ed at the broker but not on our side).
  const isRegistered = !!status?.registered;
  const isConnected = !!(status?.snaptrade?.accounts?.length);

  // ───── Auto-probe: if we're registered but not connected, run ONE silent sync.
  // Catches the case where the SnapTrade portal crashed mid-flow (the broker
  // OAuth may have actually succeeded on their side, but our local state never
  // ran the sync that would expose it). This is idempotent and rate-limit safe.
  const autoProbedRef = useRef(false);
  useEffect(() => {
    if (autoProbedRef.current) return;
    if (!isPro || !isRegistered || isConnected) return;
    autoProbedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/broker/snaptrade/sync");
        if ((data.accounts || []).length > 0 || (data.created || 0) > 0) {
          await loadStatus();
          if (onSynced) await onSynced();
        }
      } catch (e) {
        void e;
      }
    })();
  }, [isPro, isRegistered, isConnected, loadStatus, onSynced]);

  const popVictory = useCallback((created, updated, accounts) => {
    const total = (created || 0) + (updated || 0);
    if (total === 0 && (!accounts || accounts.length === 0)) return;
    // Three confetti bursts: left, center, right
    const colors = ["#d4c8b8", "#b8a5c4", "#9bb89b", "#d4af37", "#1e1e1c"]; // brand palette
    [0.2, 0.5, 0.8].forEach((x, i) => {
      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 70,
          startVelocity: 45,
          origin: { x, y: 0.7 },
          colors,
          zIndex: 9999,
        });
      }, i * 150);
    });
    const broker = accounts?.[0]?.brokerage || "Broker";
    toast.success(
      total > 0
        ? `🎉 ${broker} linked — ${total} position${total === 1 ? "" : "s"} imported`
        : `🎉 ${broker} linked — auto-sync active`,
      { duration: 5000 }
    );
  }, []);

  const runSync = useCallback(async (silent = false, withConfetti = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/broker/snaptrade/sync");
      const fresh = await loadStatus();
      const total = (data.created || 0) + (data.updated || 0);
      if (withConfetti) {
        popVictory(data.created, data.updated, data.accounts);
      } else if (!silent && total > 0) {
        toast.success(`Synced ${data.created} new · ${data.updated} updated · ${data.deleted} removed`);
      } else if (!silent && fresh?.snaptrade?.accounts?.length === 0) {
        toast.message("Sync complete — no broker linked yet");
      }
      if (onSynced) await onSynced();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [loadStatus, onSynced, popVictory]);

  // ───────── postMessage handshake from /broker/callback ─────────
  useEffect(() => {
    if (!waitingForBroker) return;
    const onMessage = (ev) => {
      if (ev.origin !== window.location.origin) return;
      if (ev.data?.type !== "snaptrade-connection-complete") return;
      setWaitingForBroker(false);
      try { brokerTabRef.current?.close(); } catch (_) { void 0; }
      runSync(true, true);
    };
    window.addEventListener("message", onMessage);
    // Fallback: poll until the broker tab is closed (user closed it manually)
    const closePoll = setInterval(() => {
      if (brokerTabRef.current?.closed) {
        clearInterval(closePoll);
        setWaitingForBroker(false);
        runSync(true, true);
      }
    }, 800);
    return () => {
      window.removeEventListener("message", onMessage);
      clearInterval(closePoll);
    };
  }, [waitingForBroker, runSync]);

  const openPortal = async () => {
    if (!isPro) { openUpgrade("broker_sync_pro_only"); return; }
    setBusy(true);
    try {
      const redirect_uri = `${window.location.origin}/broker/callback`;
      const { data } = await api.post("/broker/snaptrade/connect-url", { redirect_uri });
      const url = data.redirect_uri;
      // Open in a TAB (not popup) — user can freely switch to another tab to
      // copy broker credentials without the broker window getting dismissed.
      const tab = window.open(url, "_blank", "noopener,noreferrer");
      brokerTabRef.current = tab;
      if (!tab) {
        // Popup blocker — fall back to in-place navigation
        window.location.href = url;
        return;
      }
      setWaitingForBroker(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "broker_sync_pro_only") openUpgrade("broker_sync_pro_only");
      else toast.error(typeof detail === "string" ? detail : "Could not open broker portal");
    } finally {
      setBusy(false);
    }
  };

  const cancelWaiting = () => {
    setWaitingForBroker(false);
    try { brokerTabRef.current?.close(); } catch (_) { void 0; }
    toast.message("Broker connection cancelled");
  };

  const manualCheck = async () => {
    setWaitingForBroker(false);
    try { brokerTabRef.current?.close(); } catch (_) { void 0; }
    await runSync(false, true);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect broker? All broker-synced positions will be removed (manual entries stay).")) return;
    setBusy(true);
    try {
      await api.delete("/broker/snaptrade/disconnect");
      await loadStatus();
      toast.success("Broker disconnected");
      if (onSynced) await onSynced();
    } catch (e) {
      void e;
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  // ─────────────── Persistent "waiting" overlay ───────────────
  const WaitingOverlay = waitingForBroker ? (
    <div
      data-testid="broker-waiting-overlay"
      className="fixed inset-0 z-[9000] bg-graphite/70 backdrop-blur-sm flex items-center justify-center px-6"
    >
      <div className="bg-champagne border-2 border-paars max-w-md w-full">
        <div className="px-6 py-5 border-b border-stone flex items-center gap-3">
          <div className="w-10 h-10 bg-paars/20 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-paars-dark animate-pulse" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// AWAITING_BROKER</div>
            <div className="text-base font-semibold text-graphite">Connecting your broker…</div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-graphite/75 leading-relaxed">
          <p>
            We opened the SnapTrade connection portal in a new tab. Complete the broker login there — you can freely switch tabs to copy any credentials you need; this window will keep waiting.
          </p>
          <p className="text-xs text-graphite/55">
            We&rsquo;ll auto-detect when you&rsquo;re done and pull in your positions. If something looks stuck, click &ldquo;I&rsquo;m done — sync now&rdquo; below.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-stone flex flex-wrap gap-2">
          <button
            data-testid="broker-manual-check"
            onClick={manualCheck}
            className="px-4 py-2.5 bg-graphite text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite/90 flex items-center gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" /> I&rsquo;M DONE — SYNC NOW
          </button>
          <button
            data-testid="broker-cancel-waiting"
            onClick={cancelWaiting}
            className="px-4 py-2.5 border border-graphite/30 text-graphite/80 text-xs font-semibold tracking-wider uppercase hover:bg-stone/50"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ─────────────── Button variant (Holdings header) ───────────────
  if (variant === "button") {
    let buttonClass, label, Icon;
    if (!isPro) {
      buttonClass = "border border-gold/60 text-gold-dark bg-gold/15 hover:bg-gold/25";
      label = "BROKER (PRO)";
      Icon = Lock;
    } else if (isConnected) {
      buttonClass = "border border-groen text-groen-dark bg-groen/15 hover:bg-groen/25";
      label = syncing ? "SYNCING…" : "BROKER LINKED · RESYNC";
      Icon = syncing ? RefreshCw : ShieldCheck;
    } else if (isRegistered) {
      buttonClass = "border border-gold/60 text-gold-dark bg-gold/10 hover:bg-gold/20";
      label = syncing ? "CHECKING…" : "FINISH BROKER LINK";
      Icon = syncing ? RefreshCw : AlertCircle;
    } else {
      buttonClass = "border border-paars text-paars-dark hover:bg-paars/15";
      label = "CONNECT BROKER";
      Icon = Plug;
    }
    return (
      <>
        <button
          data-testid="broker-connect-btn"
          onClick={isConnected ? () => runSync(false, false) : openPortal}
          disabled={busy || syncing}
          title={!isPro ? "Pro feature — click to upgrade" : isConnected ? "Re-sync broker positions" : isRegistered ? "Connection started but not completed — click to retry" : "Connect a broker (IBKR, Mexem, Schwab, +30 more)"}
          className={`px-3 py-2.5 text-xs flex items-center gap-1.5 disabled:opacity-50 ${buttonClass}`}
        >
          <Icon className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {label}
        </button>
        {WaitingOverlay}
      </>
    );
  }

  // ─────────────── Card variant (Settings page) ───────────────
  return (
    <>
      <div data-testid="broker-connections-card" className="bg-champagne border border-stone">
        <div className="px-6 py-5 border-b border-stone flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-1">// BROKER LINKS</div>
            <div className="text-base font-semibold text-graphite">Connected brokers (SnapTrade)</div>
          </div>
          {!isPro ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-graphite bg-gold">
              <Lock className="w-3.5 h-3.5" strokeWidth={2.5} /> PRO ONLY
            </span>
          ) : isConnected ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-groen-dark bg-groen/20 border border-groen/40">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} /> LINKED
            </span>
          ) : isRegistered ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-gold-dark bg-gold/20 border border-gold/40">
              <AlertCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> NO ACCOUNTS
            </span>
          ) : null}
        </div>

        {!isPro ? (
          <div className="px-6 py-6">
            <div className="text-sm text-graphite/65 mb-4">
              Auto-import positions from Interactive Brokers, Mexem, Schwab, Fidelity, and 30+ other brokers.
              Read-only — we never trade or move money. Upgrade to Pro to unlock.
            </div>
            <button
              data-testid="broker-card-upgrade"
              onClick={() => openUpgrade("broker_sync_pro_only")}
              className="px-4 py-2.5 bg-paars text-graphite text-xs font-semibold tracking-wider uppercase hover:bg-paars-light flex items-center gap-2"
            >
              Upgrade to Pro
            </button>
          </div>
        ) : !isRegistered ? (
          <div className="px-6 py-6">
            <div className="text-sm text-graphite/65 mb-4">
              No broker connected. Connect once and we&rsquo;ll auto-sync positions every hour.
            </div>
            <button
              data-testid="broker-connect-card-btn"
              onClick={openPortal}
              disabled={busy}
              className="px-4 py-2.5 bg-graphite text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite/90 flex items-center gap-2 disabled:opacity-50"
            >
              <Plug className="w-3.5 h-3.5" /> {busy ? "OPENING…" : "CONNECT A BROKER"}
            </button>
          </div>
        ) : !isConnected ? (
          <div className="px-6 py-6 space-y-4">
            <div className="border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-graphite/80 flex items-start gap-3">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-gold-dark" />
              <div>
                <div className="font-semibold mb-1">Connection started, but no accounts found yet</div>
                <div className="text-xs text-graphite/65 leading-relaxed">
                  You started a broker connection but it didn&rsquo;t complete on SnapTrade&rsquo;s side — usually because the portal UI crashed
                  after you finished. Try linking again or use the buttons below.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="broker-retry-link"
                onClick={openPortal}
                disabled={busy}
                className="px-4 py-2.5 bg-paars text-graphite text-xs font-semibold tracking-wider uppercase hover:bg-paars-light flex items-center gap-2"
              >
                <Plug className="w-3.5 h-3.5" /> RETRY BROKER LINK
              </button>
              <button
                data-testid="broker-recheck-btn"
                onClick={() => runSync(false, false)}
                disabled={syncing}
                className="px-3 py-2.5 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "CHECKING…" : "CHECK FOR CONNECTIONS"}
              </button>
              <button
                data-testid="broker-disconnect-btn"
                onClick={disconnect}
                disabled={busy}
                className="px-3 py-2.5 border border-burgundy/40 text-burgundy text-xs hover:bg-burgundy/10 flex items-center gap-1.5 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" /> RESET
              </button>
            </div>
            {status?.snaptrade?.last_error && (
              <div className="border border-burgundy/40 bg-burgundy/5 px-3 py-2 text-xs text-burgundy flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-px shrink-0" />
                <span>{status.snaptrade.last_error}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm text-graphite">
                <CheckCircle2 className="w-4 h-4 text-groen-dark" />
                <span className="font-semibold">SnapTrade linked</span>
                <span className="text-graphite/55 text-xs">
                  {status?.snaptrade?.last_sync ? `· Last sync ${fmtDate(status.snaptrade.last_sync)}` : "· Awaiting first sync"}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="broker-resync-btn"
                  onClick={() => runSync(false, false)}
                  disabled={syncing}
                  className="px-3 py-2 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "SYNCING…" : "SYNC NOW"}
                </button>
                <button
                  data-testid="broker-add-more"
                  onClick={openPortal}
                  disabled={busy}
                  className="px-3 py-2 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5"
                >
                  <Link2 className="w-3.5 h-3.5" /> ADD ANOTHER
                </button>
                <button
                  data-testid="broker-disconnect-btn"
                  onClick={disconnect}
                  disabled={busy}
                  className="px-3 py-2 border border-burgundy/40 text-burgundy text-xs hover:bg-burgundy/10 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" /> DISCONNECT
                </button>
              </div>
            </div>

            {status?.snaptrade?.last_error && (
              <div className="border border-burgundy/40 bg-burgundy/5 px-3 py-2 text-xs text-burgundy flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-px shrink-0" />
                <span>{status.snaptrade.last_error}</span>
              </div>
            )}

            {status?.snaptrade?.accounts?.length > 0 && (
              <div className="border border-stone">
                <div className="bg-stone/40 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">
                  {status.snaptrade.accounts.length} ACCOUNT{status.snaptrade.accounts.length === 1 ? "" : "S"} · {status.synced_holdings_count} POSITION{status.synced_holdings_count === 1 ? "" : "S"} SYNCED
                </div>
                <div>
                  {status.snaptrade.accounts.map((a) => (
                    <div key={a.account_id} className="flex items-center justify-between px-3 py-2 border-t border-stone first:border-t-0 text-sm">
                      <div>
                        <div className="font-semibold text-graphite">{a.brokerage || "Broker"}</div>
                        <div className="text-xs text-graphite/55">{a.name || a.account_id} {a.number ? `· ${a.number}` : ""}</div>
                      </div>
                      <div className="text-xs text-graphite/60 font-mono">{a.currency || "USD"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {WaitingOverlay}
    </>
  );
}
